import type { MockedResponse } from "@apollo/client/testing";
import { COMMISSION_CAMPAIGNS_QUERY } from "../campaigns/queries";
import { COMMISSION_ENROLLMENTS_QUERY } from "../enrollments/queries";
import { COMMISSION_PARTNERS_QUERY } from "../partners/queries";
import { COMMISSION_PROGRAMS_QUERY } from "../programs/queries";
import { buildConnection } from "./connection";
import {
  commissionCampaignFixtures,
  commissionEnrollmentFixtures,
  commissionPartnerFixtures,
  commissionProgramFixtures
} from "./fixtures";

function withTypenames<T>(node: T, typename: string) {
  return { __typename: typename, ...node };
}

export const commissionProgramsMockData = {
  commissionPrograms: buildConnection(
    "BackofficeCommissionProgramConnection",
    commissionProgramFixtures.map((node) => withTypenames(node, "BackofficeCommissionProgram"))
  )
};

export const commissionPartnersMockData = {
  commissionPartners: buildConnection(
    "BackofficeCommissionPartnerConnection",
    commissionPartnerFixtures.map((node) => withTypenames(node, "BackofficeCommissionPartner"))
  )
};

export const commissionCampaignsMockData = {
  commissionCampaigns: buildConnection(
    "BackofficeCommissionCampaignConnection",
    commissionCampaignFixtures.map((node) => ({
      __typename: "BackofficeCommissionCampaign",
      ...node,
      partner: withTypenames(node.partner, "BackofficeCommissionPartner"),
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }))
  )
};

export const commissionEnrollmentsMockData = {
  commissionEnrollments: buildConnection(
    "BackofficeCommissionEnrollmentConnection",
    commissionEnrollmentFixtures.map((node) => ({
      __typename: "BackofficeCommissionEnrollment",
      ...node,
      person: node.person ? withTypenames(node.person, "BackofficeCommissionEnrollmentPerson") : null,
      program: withTypenames(node.program, "BackofficeCommissionProgramSummary")
    }))
  )
};

function createCatalogMock(query: MockedResponse["request"]["query"], data: Record<string, unknown>): MockedResponse {
  return {
    request: { query },
    maxUsageCount: Infinity,
    variableMatcher: () => true,
    result: { data }
  };
}

export const commissionProgramsApolloMock = createCatalogMock(COMMISSION_PROGRAMS_QUERY, commissionProgramsMockData);
export const commissionPartnersApolloMock = createCatalogMock(COMMISSION_PARTNERS_QUERY, commissionPartnersMockData);
export const commissionCampaignsApolloMock = createCatalogMock(COMMISSION_CAMPAIGNS_QUERY, commissionCampaignsMockData);
export const commissionEnrollmentsApolloMock = createCatalogMock(
  COMMISSION_ENROLLMENTS_QUERY,
  commissionEnrollmentsMockData
);

/** All commission catalog query mocks — use in Vitest, Storybook, or dev mock link. */
export const commissionCatalogApolloMocks: MockedResponse[] = [
  commissionProgramsApolloMock,
  commissionPartnersApolloMock,
  commissionCampaignsApolloMock,
  commissionEnrollmentsApolloMock
];

export const commissionCatalogMockResults: Record<string, Record<string, unknown>> = {
  CommissionPrograms: commissionProgramsMockData,
  CommissionPartners: commissionPartnersMockData,
  CommissionCampaigns: commissionCampaignsMockData,
  CommissionEnrollments: commissionEnrollmentsMockData
};
